/** Horizontal inset for target dashed line and line endpoints (Figma: 16px each side). */
export const LINE_CHART_PLOT_PADDING_X = 16;

/** Room for endpoint dot radius + stroke inside the SVG viewBox (right side). */
export const LINE_CHART_ENDPOINT_MARGIN_X = 10;

/** Left gutter for "$1M" / "FI target" label (Figma: 30px). */
export const LINE_CHART_LABEL_OFFSET_X = 30;

/** Default plot width at H5 design width 313 − 30 label offset. */
export const LINE_CHART_DEFAULT_PLOT_WIDTH = 283;

/** Minimum plot width for very narrow containers. */
export const LINE_CHART_MIN_PLOT_WIDTH = 200;

/** Estimated half-width for inline "N yr(s) sooner" at body_bold_s. */
export const BRACKET_LABEL_HALF_WIDTH_PX = 40;

export const lineChartInnerWidth = (
  chartWidth: number,
  plotPaddingX = LINE_CHART_PLOT_PADDING_X,
  endpointMarginX = LINE_CHART_ENDPOINT_MARGIN_X,
): number => Math.max(0, chartWidth - plotPaddingX * 2 - endpointMarginX);

/** Plot SVG width from measured chart container (H5 + PC responsive). */
export const computeLineChartPlotWidth = (
  containerWidth: number,
  labelOffsetX = LINE_CHART_LABEL_OFFSET_X,
): number => {
  if (containerWidth <= labelOffsetX) {
    return LINE_CHART_DEFAULT_PLOT_WIDTH;
  }
  return Math.max(
    LINE_CHART_MIN_PLOT_WIDTH,
    Math.round(containerWidth - labelOffsetX),
  );
};

/** Map 0–100 progress series to SVG path coordinates within chart area. */
export const buildProgressLinePath = (
  series: number[],
  width: number,
  height: number,
  paddingX = 0,
  paddingY = 0,
  endpointMarginX = LINE_CHART_ENDPOINT_MARGIN_X,
): string => {
  if (series.length === 0) {
    return "";
  }

  const innerW = lineChartInnerWidth(width, paddingX, endpointMarginX);
  const innerH = height - paddingY * 2;
  const stepX = series.length > 1 ? innerW / (series.length - 1) : 0;

  const points = series.map((value, index) => {
    const x = paddingX + index * stepX;
    const clamped = Math.max(0, Math.min(100, value));
    const y = paddingY + innerH * (1 - clamped / 100);
    return { x, y };
  });

  return points
    .map((point, index) =>
      index === 0
        ? `M ${point.x.toFixed(1)} ${point.y.toFixed(1)}`
        : `L ${point.x.toFixed(1)} ${point.y.toFixed(1)}`,
    )
    .join(" ");
};

export const lastPoint = (
  series: number[],
  width: number,
  height: number,
  paddingX = 0,
  paddingY = 0,
  endpointMarginX = LINE_CHART_ENDPOINT_MARGIN_X,
): { x: number; y: number } | null => {
  if (series.length === 0) {
    return null;
  }

  const innerW = lineChartInnerWidth(width, paddingX, endpointMarginX);
  const innerH = height - paddingY * 2;
  const stepX = series.length > 1 ? innerW / (series.length - 1) : 0;
  const index = series.length - 1;
  const value = series[index] ?? 0;
  const clamped = Math.max(0, Math.min(100, value));

  return {
    x: paddingX + index * stepX,
    y: paddingY + innerH * (1 - clamped / 100),
  };
};

/** Map target year to endpoint X within the plot area (includes plot padding). */
export const resolveLineChartEndX = (
  onYourOwnYear: number,
  endYear: number,
  chartWidth: number,
  plotPaddingX = LINE_CHART_PLOT_PADDING_X,
  currentYear = new Date().getFullYear(),
  endpointMarginX = LINE_CHART_ENDPOINT_MARGIN_X,
): number => {
  const innerW = lineChartInnerWidth(chartWidth, plotPaddingX, endpointMarginX);
  const ownSpan = Math.max(1, onYourOwnYear - currentYear);
  const endSpan = Math.max(0, endYear - currentYear);
  return plotPaddingX + (endSpan / ownSpan) * innerW;
};

/** Path width so `lastPoint` lands on `endX` when using symmetric plot padding. */
export const pathWidthForLineEndX = (
  endX: number,
  plotPaddingX = LINE_CHART_PLOT_PADDING_X,
): number => endX + plotPaddingX;

/** @deprecated Prefer resolveLineChartEndX + pathWidthForLineEndX. */
export const resolveLineChartSpanWidth = (
  onYourOwnYear: number,
  endYear: number,
  fullWidth: number,
  currentYear = new Date().getFullYear(),
): number =>
  pathWidthForLineEndX(
    resolveLineChartEndX(
      onYourOwnYear,
      endYear,
      fullWidth,
      LINE_CHART_PLOT_PADDING_X,
      currentYear,
    ),
    LINE_CHART_PLOT_PADDING_X,
  );

/** Keep bracket label center within the plot safe area for single-line labels. */
export const clampBracketLabelCenterX = (
  bracketMidX: number,
  chartOffsetX: number,
  chartWidth: number,
  plotPaddingX = LINE_CHART_PLOT_PADDING_X,
  labelHalfWidth = BRACKET_LABEL_HALF_WIDTH_PX,
): number => {
  const centerX = chartOffsetX + bracketMidX;
  const minCenter = chartOffsetX + plotPaddingX + labelHalfWidth;
  const maxCenter = chartOffsetX + chartWidth - plotPaddingX - labelHalfWidth;
  return Math.max(minCenter, Math.min(maxCenter, centerX));
};
